import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatError } from "@/api";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, CheckCircle2, Users, BarChart3, Bell } from "lucide-react";

export default function LoginPage() {
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [remember, setRemember] = useState(true);
    const [loading, setLoading] = useState(false);
    const { login, register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (mode === "login") {
                await login(email, password, remember);
            } else {
                if (!name.trim()) { toast.error("Introduceți numele"); setLoading(false); return; }
                await register(name, email, password);
            }
            navigate("/");
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    const features = [
        { icon: <CheckCircle2 size={15} />, text: "Task management vizual cu drag & drop" },
        { icon: <Users size={15} />, text: "Workspace-uri, echipe și board-uri structurate" },
        { icon: <BarChart3 size={15} />, text: "Dashboard managerial și rapoarte operaționale" },
        { icon: <Bell size={15} />, text: "Notificări in-app și alerte desktop" },
    ];

    return (
        <div className="min-h-screen flex relative overflow-hidden bg-[#0A0A0F]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {/* Liquid animated background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-1/3 -left-1/4 w-[700px] h-[700px] rounded-full opacity-50 mix-blend-screen blur-3xl animate-liquid-1"
                    style={{ background: "radial-gradient(circle, rgba(96,165,250,0.25), rgba(96,165,250,0) 60%)" }} />
                <div className="absolute -bottom-1/3 -right-1/4 w-[800px] h-[800px] rounded-full opacity-60 mix-blend-screen blur-3xl animate-liquid-2"
                    style={{ background: "radial-gradient(circle, rgba(167,139,250,0.20), rgba(167,139,250,0) 65%)" }} />
                <div className="absolute top-1/2 left-1/3 w-[500px] h-[500px] rounded-full opacity-40 mix-blend-screen blur-3xl animate-liquid-3"
                    style={{ background: "radial-gradient(circle, rgba(52,211,153,0.18), rgba(52,211,153,0) 60%)" }} />
                {/* Subtle grid overlay */}
                <div className="absolute inset-0 opacity-[0.025]" style={{
                    backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "44px 44px"
                }} />
                {/* Noise grain */}
                <div className="absolute inset-0 opacity-[0.04]" style={{
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%222%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/%3E%3C/svg%3E")'
                }} />
            </div>

            {/* Left Panel — brand & features */}
            <div className="hidden lg:flex lg:w-[55%] relative z-10">
                <div className="flex flex-col justify-between p-14 w-full">
                    <div className="flex items-center">
                        <img src="/logo-tom-v3.png" alt="TOM — Team Operations Manager" className="h-20 w-auto object-contain drop-shadow-[0_0_30px_rgba(96,165,250,0.35)]" />
                    </div>

                    <div className="max-w-lg">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-medium">Sistem operațional intern</span>
                        </div>
                        <h1 className="text-[2.75rem] leading-[1.05] font-bold text-white mb-5 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            Control operațional<br />
                            <span className="bg-gradient-to-r from-white via-white to-white/40 bg-clip-text text-transparent">complet al echipei tale.</span>
                        </h1>
                        <p className="text-white/55 text-[15px] leading-relaxed mb-10 max-w-md">
                            Centralizează taskuri, responsabilități și raportare într-o interfață rapidă, focusată pe livrare.
                        </p>
                        <div className="space-y-3.5">
                            {features.map((f, i) => (
                                <div key={i} className="flex items-center gap-3 group">
                                    <div className="w-7 h-7 rounded-md bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-white/60 group-hover:bg-white/[0.07] group-hover:text-white/90 transition-colors">
                                        {f.icon}
                                    </div>
                                    <span className="text-sm text-white/65 group-hover:text-white/85 transition-colors">{f.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="text-white/30 text-[11px] tracking-wide">&copy; 2026 TOM — Team Operations Manager.</div>
                </div>
            </div>

            {/* Right Panel — auth form */}
            <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 relative z-10">
                <div className="w-full max-w-[420px]">
                    {/* Mobile branding */}
                    <div className="flex items-center justify-center mb-8 lg:hidden">
                        <img src="/logo-tom-v3.png" alt="TOM — Team Operations Manager" className="h-14 w-auto object-contain" />
                    </div>

                    {/* Glass card */}
                    <div className="relative">
                        <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-white/[0.12] to-white/[0.02] opacity-80" />
                        <div className="relative bg-white/[0.03] backdrop-blur-2xl rounded-2xl border border-white/[0.08] p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]">
                            <h2 className="text-[26px] font-bold text-white mb-1.5 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                {mode === "login" ? "Bun venit înapoi" : "Creează cont nou"}
                            </h2>
                            <p className="text-sm text-white/45 mb-7">
                                {mode === "login" ? "Conectează-te la platforma operațională." : "Înregistrează-te ca să accesezi platforma."}
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
                                {mode === "register" && (
                                    <div>
                                        <label className="block text-[11px] font-medium text-white/55 mb-1.5 uppercase tracking-[0.12em]">Nume complet</label>
                                        <input
                                            data-testid="register-name-input"
                                            type="text"
                                            value={name}
                                            onChange={e => setName(e.target.value)}
                                            placeholder="Ana Popescu"
                                            className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:bg-white/[0.06] focus:border-white/30 transition-all"
                                            required
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-[11px] font-medium text-white/55 mb-1.5 uppercase tracking-[0.12em]">Email</label>
                                    <input
                                        data-testid="email-input"
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder="ana@companie.ro"
                                        className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:bg-white/[0.06] focus:border-white/30 transition-all"
                                        required
                                        autoComplete="email"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-medium text-white/55 mb-1.5 uppercase tracking-[0.12em]">Parolă</label>
                                    <div className="relative">
                                        <input
                                            data-testid="password-input"
                                            type={showPass ? "text" : "password"}
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:bg-white/[0.06] focus:border-white/30 transition-all pr-10"
                                            required
                                            autoComplete={mode === "login" ? "current-password" : "new-password"}
                                        />
                                        <button type="button" onClick={() => setShowPass(!showPass)}
                                            data-testid="toggle-password-visibility"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors">
                                            {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                                        </button>
                                    </div>
                                </div>

                                {mode === "login" && (
                                    <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1" data-testid="remember-me-label">
                                        <span className="relative inline-flex">
                                            <input
                                                type="checkbox"
                                                data-testid="remember-me-checkbox"
                                                checked={remember}
                                                onChange={e => setRemember(e.target.checked)}
                                                className="peer sr-only"
                                            />
                                            <span className="w-4 h-4 rounded border border-white/25 bg-white/[0.04] peer-checked:bg-white peer-checked:border-white transition-all flex items-center justify-center">
                                                {remember && (
                                                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                                                        <path d="M2 6L4.5 8.5L10 3" stroke="#0A0A0F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </span>
                                        </span>
                                        <span className="text-xs text-white/65">Ține-mă conectat pe acest device <span className="text-white/35">(30 zile)</span></span>
                                    </label>
                                )}

                                <button
                                    data-testid="auth-submit-btn"
                                    type="submit"
                                    disabled={loading}
                                    className="group w-full relative flex items-center justify-center gap-2 bg-white/[0.08] hover:bg-white/[0.14] text-white py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 mt-2 overflow-hidden border border-white/15 hover:border-white/30 shadow-lg shadow-black/30"
                                >
                                    {loading ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <span>{mode === "login" ? "Conectare" : "Înregistrare"}</span>
                                            <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <p className="text-xs text-white/45">
                                    {mode === "login" ? "Nu ai cont?" : "Ai deja cont?"}{" "}
                                    <button
                                        data-testid="auth-toggle-mode"
                                        onClick={() => setMode(mode === "login" ? "register" : "login")}
                                        className="text-white/85 font-semibold hover:text-white transition-colors"
                                    >
                                        {mode === "login" ? "Înregistrează-te" : "Conectează-te"}
                                    </button>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
